import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AriaOrb } from "../components/AriaOrb";
import { useSpeech } from "../hooks/useSpeech";
import { ReplayCoach } from "../components/ReplayCoach";
import { CoachHistory } from "../components/CoachHistory";
import { Sunrise, Loader2, ChevronLeft, ChevronRight, Volume2, Square, RefreshCw, Eye, CheckCircle2, Youtube, StickyNote, BookOpen, FileText, Pause, Play, Mic } from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };

export default function Warmup() {
  const navigate = useNavigate();
  const [warmup, setWarmup] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [coaching, setCoaching] = useState(null);
  const [coachKey, setCoachKey] = useState(0);
  const timerRef = useRef(null);
  const { speak, speakSequence, stop, speaking, loadingKey } = useSpeech();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/warmup"); setWarmup(data.warmup); setEvent(data.event); } catch (err) { console.error("[ARIA]", err); }
      finally { setLoading(false); }
    })();
  }, []);  

  const generate = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/warmup/generate"); setWarmup(data.warmup); setEvent(data.event); setIdx(0); setRunning(false); toast.success("Warm-up ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build the warm-up"); }
    finally { setGenerating(false); }
  };

  const block = warmup?.blocks?.[idx];
  useEffect(() => { if (block) { setRemaining(block.minutes * 60); setRevealed({}); setCoaching(null); } }, [idx, warmup]); // eslint-disable-line

  useEffect(() => {
    if (!running) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setRemaining(r => {
      if (r <= 1) { clearInterval(timerRef.current); setRunning(false); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);  

  const go = (n) => { stop(); setIdx(Math.max(0, Math.min((warmup?.blocks?.length || 1) - 1, n))); };

  const readBlock = () => {
    if (!block) return;
    if (speaking) { stop(); return; }
    const items = [[block.text, `b-${block.id}`]];
    (block.items || []).forEach((it, i) => {
      const t = block.kind === "closed_gaps" ? `${it.concept}. ${it.what_you_learned || it.what_was_missing}`
        : block.kind === "strong_answers" ? `${it.concept}. ${it.answer}`
        : block.kind === "recall" ? `${it.concept}. ${it.prompt}` : "";
      if (t) items.push([t, `i-${block.id}-${i}`]);
    });
    speakSequence(items);
  };

  if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--aria-primary)]" /></div>;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const total = warmup?.blocks?.length || 0;

  return (
    <div className="h-screen overflow-y-auto bg-[#030711] text-white" data-testid="warmup-page">
      <div className="max-w-3xl mx-auto p-8 lg:p-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-4">
            <AriaOrb size={48} state={speaking ? "speaking" : generating ? "thinking" : "idle"} />
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--aria-primary-glow)] font-semibold flex items-center gap-1.5"><Sunrise size={12} /> Interview day</div>
              <h1 className="font-display text-3xl">15-minute warm-up</h1>
              {event && <div className="text-sm text-slate-400 mt-0.5" data-testid="warmup-event">{event.title} · {new Date(event.due_date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>}
            </div>
          </div>
          <button onClick={generate} disabled={generating} data-testid="warmup-generate-button"
            className="btn px-4 py-2 text-sm border border-white/15 text-slate-300 hover:text-white flex items-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {warmup ? "Refresh from latest interviews" : "Build my warm-up"}
          </button>
        </div>

        {!warmup && (
          <div className="aria-card p-10 text-center" data-testid="warmup-empty">
            <div className="font-display text-xl mb-2">Nothing to replay yet</div>
            <p className="text-sm text-slate-400 max-w-md mx-auto">The warm-up is built from your real interviews — the gaps you closed and your strongest answers. Run a mock interview first, then come back here on the morning of the real one.</p>
          </div>
        )}

        <CoachHistory refreshKey={coachKey} />

        {warmup && block && (
          <>
            <div className="flex items-center gap-2 mb-6" data-testid="warmup-progress">
              {warmup.blocks.map((b, i) => (
                <button key={b.id} onClick={() => go(i)} data-testid={`warmup-step-${b.id}`}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${i < idx ? "bg-emerald-400/70" : i === idx ? "bg-[var(--aria-primary)]" : "bg-white/10"}`} title={`${b.title} · ${b.minutes} min`} />
              ))}
            </div>

            <div className="aria-card p-8 ring-glow-primary fade-in" key={block.id} data-testid={`warmup-block-${block.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Step {idx + 1} of {total} · {block.minutes} min</div>
                  <h2 className="font-display text-2xl">{block.title}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-2xl text-[var(--aria-primary-glow)] tabular-nums" data-testid="warmup-timer">{mm}:{ss}</span>
                  <button onClick={() => setRunning(!running)} data-testid="warmup-timer-toggle" className="p-2 rounded-md border border-white/15 hover:border-[var(--aria-primary)]">
                    {running ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <button onClick={readBlock} data-testid="warmup-read-aloud"
                    className={`p-2 rounded-md border ${speaking ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-white/15 hover:border-[var(--aria-primary)]"}`}>
                    {loadingKey ? <Loader2 size={14} className="animate-spin" /> : speaking ? <Square size={14} /> : <Volume2 size={14} />}
                  </button>
                </div>
              </div>

              <p className="text-[15px] leading-relaxed text-slate-200 whitespace-pre-line mb-6" data-testid="warmup-block-text">{block.text}</p>

              {block.kind === "closed_gaps" && (
                <div className="space-y-3">
                  {block.items.length === 0 && <div className="text-sm text-slate-400">No closed gaps recorded yet — the next time ARIA finds and you fix a gap in an interview, it will appear here.</div>}
                  {block.items.map((g, i) => (
                    <div key={`${g.concept}-${g.date || i}`} className="rounded-md bg-white/5 border border-white/10 p-4" data-testid={`warmup-closed-gap-${i}`}>
                      <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={14} className="text-emerald-400" /><span className="font-display text-lg">{g.concept}</span></div>
                      {g.what_was_missing && <div className="text-xs text-amber-300/90 mb-1.5">Was missing: {g.what_was_missing}</div>}
                      {g.what_you_learned && <div className="text-sm text-slate-200 leading-relaxed">{g.what_you_learned}</div>}
                      {g.sources?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-400">
                          {g.sources.map((s, j) => { const I = SOURCE_ICON[s.source_type] || FileText; return <span key={j} className="inline-flex items-center gap-1"><I size={10} className="text-[var(--aria-primary-glow)]" />{s.source_title}</span>; })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {block.kind === "strong_answers" && (
                <div className="space-y-3">
                  {block.items.length === 0 && <div className="text-sm text-slate-400">No strong answers captured yet — answer well in a mock interview and they'll be replayed here.</div>}
                  {block.items.map((a, i) => (
                    <div key={`${a.concept}-${a.date || i}`} className="rounded-md bg-white/5 border border-white/10 p-4" data-testid={`warmup-strong-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-display text-lg">{a.concept}</span>
                        <span className="aria-pill aria-pill-success text-[10px]">Level {a.level}</span>
                      </div>
                      <div className="text-xs text-slate-400 mb-2">Q · {a.question}</div>
                      <blockquote className="text-sm text-slate-100 leading-relaxed border-l-2 border-[var(--aria-primary)] pl-3 italic">“{a.answer}”</blockquote>
                      {a.feedback && <div className="text-xs text-emerald-300/90 mt-2">ARIA: {a.feedback}</div>}
                      <div className="mt-2 flex items-center gap-4">
                        <button onClick={() => speak(a.answer, `sa-${i}`)} data-testid={`warmup-strong-listen-${i}`} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"><Volume2 size={10} /> Hear it back</button>
                        <button onClick={() => setCoaching(coaching === i ? null : i)} data-testid={`warmup-strong-rerecord-${i}`} className="text-[11px] text-[var(--aria-primary-glow)] hover:underline inline-flex items-center gap-1"><Mic size={10} /> Re-record & compare</button>
                      </div>
                      {coaching === i && <ReplayCoach answer={a} onClose={() => setCoaching(null)} onResult={() => setCoachKey(k => k + 1)} />}
                    </div>
                  ))}
                </div>
              )}

              {block.kind === "recall" && (
                <div className="space-y-3">
                  {block.items.length === 0 && <div className="text-sm text-slate-400">Recall prompts appear once you have concepts at level 3 or higher.</div>}
                  {block.items.map((r, i) => (
                    <div key={`${r.concept}-${r.prompt?.slice(0, 24)}`} className="rounded-md bg-white/5 border border-white/10 p-4" data-testid={`warmup-recall-${i}`}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-primary-glow)] mb-1">{r.concept}</div>
                      <div className="text-sm text-slate-100 mb-3">{r.prompt}</div>
                      {revealed[i] ? (
                        <div className="text-sm text-emerald-200/90 leading-relaxed border-l-2 border-emerald-400 pl-3" data-testid={`warmup-recall-answer-${i}`}>{r.answer}</div>
                      ) : (
                        <button onClick={() => setRevealed(v => ({ ...v, [i]: true }))} data-testid={`warmup-recall-reveal-${i}`} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5 border border-white/15 rounded px-2.5 py-1"><Eye size={12} /> Reveal</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {block.id === "close" && block.logistics && (
                <div className="text-xs text-slate-400 border-t border-white/10 pt-3 mt-2" data-testid="warmup-logistics">Notes: {block.logistics}</div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6">
              <button onClick={() => go(idx - 1)} disabled={idx === 0} data-testid="warmup-prev" className="text-sm text-slate-400 hover:text-white disabled:opacity-30 inline-flex items-center gap-1"><ChevronLeft size={14} /> Back</button>
              {idx < total - 1 ? (
                <button onClick={() => go(idx + 1)} data-testid="warmup-next" className="btn btn-primary px-5 py-2 text-sm inline-flex items-center gap-1.5">Next <ChevronRight size={14} /></button>
              ) : (
                <button onClick={() => { stop(); navigate("/app/home"); }} data-testid="warmup-finish" className="btn btn-primary px-5 py-2 text-sm">You're ready. Go.</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
